/**
 * Editable schedule-import preview (teams list + games table), shared by the
 * division page's Upload Schedule dialog (INT-0026) and the create wizard's
 * "I have a schedule" step (INT-0051). Pure presentation over a `Draft`; every
 * edit goes through the schedulePreview helpers so renames propagate and
 * deletions cascade. Nothing here persists anything.
 */
import React from 'react';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  renameTeam, deleteTeam, updateGame, deleteGame, importableGames,
  type Draft, type DraftGame,
} from '@/containers/DivisionPage/schedulePreview';

export const ROUND_LABELS: Record<string, string> = {
  quarterfinal: 'Quarterfinal', semifinal: 'Semifinal', championship: 'Championship',
  third_place: '3rd Place', fifth_place: '5th Place', seventh_place: '7th Place',
  consolation: 'Consolation', consolation_semifinal: 'Consolation SF', '-': 'Playoff',
};

const numeric = (v: string) => (v === '' ? null : Number(v.replace(/[^0-9]/g, '') || 0));

export default function ScheduleDraftEditor({
  draft,
  onChange,
  errorByGame,
  idPrefix = 'schedule-draft',
}: {
  draft: Draft;
  onChange: (next: Draft) => void;
  /** First blocking message per game index (from validateDraft). */
  errorByGame: Map<number, string>;
  idPrefix?: string;
}) {
  const importGames = importableGames(draft);
  const newTeamCount = draft.teams.filter((t) => !t.matchedTeamId).length;

  // One side of a game row: a team dropdown, or a text input when the side is
  // a playoff placeholder ("Winner of SF1") — placeholders never become teams.
  const sideCell = (g: DraftGame, i: number, side: 'home' | 'away') => {
    const placeholderKey = side === 'home' ? 'homePlaceholder' : 'awayPlaceholder';
    const teamKey = side === 'home' ? 'homeTeam' : 'awayTeam';
    if (g[placeholderKey] != null) {
      return (
        <Input
          className="h-8 w-full min-w-[9rem] italic"
          value={g[placeholderKey] || ''}
          placeholder="e.g. Winner of SF1"
          title="Playoff placeholder: filled in once the matchup is decided"
          aria-label={`${side === 'home' ? 'Home' : 'Away'} placeholder, game ${i + 1}`}
          onChange={(e) => onChange(updateGame(draft, i, { [placeholderKey]: e.target.value }))}
        />
      );
    }
    return (
      <select
        className="h-8 w-full min-w-[9rem] rounded-md border border-input bg-background px-2 text-sm"
        value={g[teamKey]}
        aria-label={`${side === 'home' ? 'Home' : 'Away'} team, game ${i + 1}`}
        onChange={(e) => onChange(updateGame(draft, i, { [teamKey]: e.target.value }))}
      >
        {!draft.teams.some((t) => t.name === g[teamKey]) && <option value={g[teamKey]}>{g[teamKey]}</option>}
        {draft.teams.map((t) => <option key={t.name} value={t.name}>{t.name}</option>)}
      </select>
    );
  };

  return (
    <div className="space-y-6" data-testid={`${idPrefix}-editor`}>
      {/* Teams */}
      <section>
        <h3 className="mb-2 text-sm font-semibold">
          Teams <span className="font-normal text-muted-foreground">({draft.teams.length}, {newTeamCount} new)</span>
        </h3>
        <div className="space-y-1.5">
          {draft.teams.map((t, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input
                value={t.name}
                className="h-8"
                aria-label={`Team ${i + 1} name`}
                onChange={(e) => onChange(renameTeam(draft, i, e.target.value))}
              />
              {t.matchedTeamId ? (
                <Badge variant="secondary" className="shrink-0">existing</Badge>
              ) : (
                <Badge className="shrink-0">new</Badge>
              )}
              <Button
                type="button"
                variant="ghost" size="icon" className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                aria-label={`Remove team ${t.name}`}
                onClick={() => onChange(deleteTeam(draft, i))}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      </section>

      {/* Games */}
      <section>
        <h3 className="mb-2 text-sm font-semibold">
          Games <span className="font-normal text-muted-foreground">
            ({importGames.length} to import{draft.games.length !== importGames.length ? `, ${draft.games.length - importGames.length} duplicates skipped` : ''})
          </span>
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="py-1.5 pr-2 font-medium">Home</th>
                <th className="py-1.5 pr-2 font-medium">Away</th>
                <th className="py-1.5 pr-2 font-medium">Date</th>
                <th className="py-1.5 pr-2 font-medium">Time</th>
                <th className="py-1.5 pr-2 font-medium">Venue</th>
                <th className="py-1.5 pr-2 font-medium">Score</th>
                <th className="py-1.5" />
              </tr>
            </thead>
            <tbody>
              {draft.games.map((g, i) => {
                const rowError = errorByGame.get(i);
                return (
                  <tr key={i} className={`border-b last:border-0 ${g.duplicate ? 'opacity-50' : ''}`}>
                    <td className="py-1 pr-2">{sideCell(g, i, 'home')}</td>
                    <td className="py-1 pr-2">{sideCell(g, i, 'away')}</td>
                    <td className="py-1 pr-2">
                      <Input
                        type="date"
                        aria-label={`Date, game ${i + 1}`}
                        className={`h-8 w-36 ${!g.date && !g.duplicate ? 'border-destructive' : ''}`}
                        value={g.date || ''}
                        onChange={(e) => onChange(updateGame(draft, i, { date: e.target.value || null }))}
                      />
                    </td>
                    <td className="py-1 pr-2">
                      <Input
                        className="h-8 w-24"
                        placeholder="7:00 PM"
                        aria-label={`Time, game ${i + 1}`}
                        value={g.time || ''}
                        onChange={(e) => onChange(updateGame(draft, i, { time: e.target.value || null }))}
                      />
                    </td>
                    <td className="py-1 pr-2">
                      <Input
                        className="h-8 w-32"
                        aria-label={`Venue, game ${i + 1}`}
                        value={g.venue || ''}
                        onChange={(e) => onChange(updateGame(draft, i, { venue: e.target.value || null }))}
                      />
                    </td>
                    <td className="py-1 pr-2">
                      <div className="flex items-center gap-1">
                        <Input
                          type="text" inputMode="numeric" className="h-8 w-12 px-1 text-center"
                          aria-label={`Home score, game ${i + 1}`}
                          value={g.homeScore ?? ''}
                          onChange={(e) => onChange(updateGame(draft, i, { homeScore: numeric(e.target.value) }))}
                        />
                        <span className="text-muted-foreground">–</span>
                        <Input
                          type="text" inputMode="numeric" className="h-8 w-12 px-1 text-center"
                          aria-label={`Away score, game ${i + 1}`}
                          value={g.awayScore ?? ''}
                          onChange={(e) => onChange(updateGame(draft, i, { awayScore: numeric(e.target.value) }))}
                        />
                      </div>
                    </td>
                    <td className="py-1">
                      <div className="flex items-center gap-1">
                        {g.playoffRound && <Badge variant="outline">{ROUND_LABELS[g.playoffRound] || 'Playoff'}</Badge>}
                        {g.duplicate && <Badge variant="secondary">duplicate (skipped)</Badge>}
                        {rowError && <Badge variant="destructive">{rowError}</Badge>}
                        <Button
                          type="button"
                          variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive"
                          aria-label={`Remove game ${i + 1}`}
                          onClick={() => onChange(deleteGame(draft, i))}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
